import React, { useState, useEffect } from 'react';
import { galleryAPI, ServiceType, ServiceSubType, GalleryImage } from '../../services/galleryApi';
import toast from 'react-hot-toast';
import { useNavigate } from 'react-router-dom';
import Layout from '../../components/Layout';
import { PlusIcon } from '@heroicons/react/24/outline';

const ServicesGallery: React.FC = () => {
  const [serviceTypes, setServiceTypes] = useState<ServiceType[]>([]);
  const [selectedType, setSelectedType] = useState<ServiceType | null>(null);
  const [subTypes, setSubTypes] = useState<ServiceSubType[]>([]);
  const [loadingTypes, setLoadingTypes] = useState(true);
  const [loadingSubTypes, setLoadingSubTypes] = useState(false);
  const [typePreviews, setTypePreviews] = useState<{ [typeId: string]: GalleryImage[] }>({});
  const [subTypePreviews, setSubTypePreviews] = useState<{ [subTypeId: string]: GalleryImage[] }>({});

  const navigate = useNavigate();

  useEffect(() => {
    const fetchTypesAndPreviews = async () => {
      try {
        setLoadingTypes(true);
        const types = await galleryAPI.getServiceTypes();
        setServiceTypes(types);

        const newTypePreviews: { [typeId: string]: GalleryImage[] } = {};
        for (const type of types) {
          const imagesResponse = await galleryAPI.getGalleryImages({ serviceType: type._id, limit: 3 });
          newTypePreviews[type._id] = imagesResponse.images;
        }
        setTypePreviews(newTypePreviews);

      } catch (error) {
        console.error('Error fetching service types or previews:', error);
        toast.error('Erreur lors du chargement des types de service ou des aperçus.');
      } finally {
        setLoadingTypes(false);
      }
    };
    fetchTypesAndPreviews();
  }, []);

  useEffect(() => {
    if (selectedType) {
      const fetchSubTypesAndPreviews = async () => {
        try {
          setLoadingSubTypes(true);
          const fetchedSubTypes = await galleryAPI.getServiceSubTypes(selectedType._id);
          setSubTypes(fetchedSubTypes);

          const newSubTypePreviews: { [subTypeId: string]: GalleryImage[] } = {};
          for (const subType of fetchedSubTypes) {
            const imagesResponse = await galleryAPI.getGalleryImages({ serviceSubType: subType._id, limit: 3 });
            newSubTypePreviews[subType._id] = imagesResponse.images;
          }
          setSubTypePreviews(newSubTypePreviews);

        } catch (error) {
          console.error('Error fetching service sub-types or previews:', error);
          toast.error('Erreur lors du chargement des sous-types de service ou des aperçus.');
        } finally {
          setLoadingSubTypes(false);
        }
      };
      fetchSubTypesAndPreviews();
    } else {
      setSubTypes([]);
      setSubTypePreviews({});
    }
  }, [selectedType]);

  return (
    <Layout>
      <div className="container mx-auto p-4">
        <div className="flex justify-between items-center mb-6">
          <h1 className="text-2xl font-bold">Gestion de la Galerie par Services</h1>
          <button
            className="btn btn-primary"
            onClick={() => navigate('/tech/add-new-gallery')}
          >
            <PlusIcon className="h-5 w-5 mr-2" />
            Ajouter une image
          </button>
        </div>

        {loadingTypes ? (
          <p>Chargement des types de service...</p>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {serviceTypes.map((type) => (
              <div key={type._id} className="card bg-base-100 shadow-xl">
                <div className="card-body">
                  <h2 className="card-title flex justify-between items-center">
                    {type.name}
                    <button
                      className="btn btn-sm btn-ghost"
                      onClick={() => setSelectedType(selectedType?._id === type._id ? null : type)}
                    >
                      {selectedType?._id === type._id ? 'Masquer les sous-types' : 'Voir les sous-types'}
                    </button>
                  </h2>
                  <p>{type.description}</p>

                  {/* Image Previews for Type */}
                  {typePreviews[type._id] && typePreviews[type._id].length > 0 && (
                    <div className="mt-4 grid grid-cols-3 gap-2">
                      {typePreviews[type._id].map(image => (
                        <img key={image._id} src={image.url} alt={image.filename} className="w-full h-20 object-cover rounded-md" />
                      ))}
                    </div>
                  )}
                  {(!typePreviews[type._id] || typePreviews[type._id].length === 0) && (
                    <p className="text-sm text-gray-500 mt-2">Aucune image pour ce type.</p>
                  )}

                  <div className="card-actions justify-end mt-4">
                    <button
                      className="btn btn-primary"
                      onClick={() => navigate(`/tech/gallery-viewer/${type._id}`)}
                    >
                      Voir la galerie
                    </button>
                  </div>

                  {selectedType?._id === type._id && (
                    <div className="mt-4 border-t pt-4">
                      <h3 className="text-lg font-semibold mb-2">Sous-types:</h3>
                      {loadingSubTypes ? (
                        <p>Chargement des sous-types...</p>
                      ) : subTypes.length > 0 ? (
                        <ul className="space-y-2">
                          {subTypes.map((subType) => (
                            <li key={subType._id} className="flex flex-col items-start bg-gray-50 p-2 rounded-md">
                              <span className="font-medium">{subType.name}</span>
                              {/* Image Previews for SubType */}
                              {subTypePreviews[subType._id] && subTypePreviews[subType._id].length > 0 && (
                                <div className="mt-2 grid grid-cols-3 gap-1 w-full">
                                  {subTypePreviews[subType._id].map(image => (
                                    <img key={image._id} src={image.url} alt={image.filename} className="w-full h-16 object-cover rounded-md" />
                                  ))}
                                </div>
                              )}
                              {(!subTypePreviews[subType._id] || subTypePreviews[subType._id].length === 0) && (
                                <p className="text-xs text-gray-500 mt-1">Aucune image pour ce sous-type.</p>
                              )}
                              <button
                                className="btn btn-xs btn-outline mt-2"
                                onClick={() => navigate(`/tech/gallery-viewer/${subType._id}`)}
                              >
                                Voir la galerie du sous-type
                              </button>
                            </li>
                          ))}
                        </ul>
                      ) : (
                        <p className="text-center text-gray-500 py-4">Aucun sous-type trouvé pour ce service.</p>
                      )}
                    </div>
                  )}
                </div>
              </div>
            ))}
          </div>
        )}

        
      </div>
    </Layout>
  );
};

export default ServicesGallery;
